import React, { useEffect, useState } from "react";
import styles from "./SocialPostManager.module.css";
import axios from "axios";
import { toQueryString } from "../../../util/utilFunctions";
import { smTypes } from "../../../util/flStates";
import CircularProgress from "@mui/material/CircularProgress"; // For loader
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "react-toastify"; // For toast notifications
import Dialog from "@mui/material/Dialog"; // Modal component
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button"; // Button component

function SocialPostManager() {
  const { accessToken } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [postData, setPostData] = useState({ url: "" });
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [errorMessage, setErrorMessage] = useState(""); // Error message state
  const [openDialog, setOpenDialog] = useState(false); // Modal state
  const [selectedPostIndex, setSelectedPostIndex] = useState(null); // Index of selected post for deletion

  const handleInputChange = (e) => {
    setPostData({ ...postData, [e.target.name]: e.target.value });
  };

  const addPost = async () => {
    if (!postData.url.trim()) {
      setErrorMessage("Post URL cannot be empty.");
      return;
    }

    setIsLoading(true); // Show loader
    setErrorMessage(""); // Reset error message

    // console.log("Upload post clicked");
    try {
      const requestData = {
        typeSMId: smTypes.instagramPost,
        url: postData.url,
      };
      // console.log(requestData);
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/add`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        if (editIndex !== null) {
          const updatedPosts = [...posts];
          updatedPosts[editIndex] = postData;
          setPosts(updatedPosts);
          setEditIndex(null);
          toast.success("Post updated successfully!");
        } else {
          setPosts([...posts, postData]);
          toast.success("Post added successfully!");
        }
        setPostData({ url: "" });
      } else {
        const errorMsg =
          response.data.message || "Failed to upload the social media post.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Error uploading the social media post. Please try again!";
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false); // Hide loader
    }
  };

  const editPost = (index) => {
    setEditIndex(index);
    setPostData(posts[index]);
  };

  // Open confirmation modal before deletion
  const handleDeleteClick = (index) => {
    // console.log("Settig setSelectedPostIndex", index);
    setSelectedPostIndex(index);
    setOpenDialog(true);
  };

  // Close modal
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPostIndex(null);
  };

  const confirmDelete = async () => {
    try {
      const requestData = {
        id: selectedPostIndex,
      };

      // console.log(requestData);
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/delete/`,
        requestData,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      if (response.status === 200 && response.data && response.data.success) {
        setPosts(posts.filter((post) => post.id !== selectedPostIndex)); // Update posts state
        toast.success("Post deleted successfully!");
        handleCloseDialog(); // Close the confirmation dialog
      } else {
        const errorMsg =
          response.data.message || "Failed to delete the Instagram post.";
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      // console.log(error);
      toast.error("Failed to delete the post. Please try again.");
    }
  };

  // Extract the post ID from the URL for embedding
  const getEmbedUrl = (url) => {
    const postId = url.split("/").slice(-2, -1)[0]; // Extract the Instagram post ID from the URL
    return `https://www.instagram.com/p/${postId}/embed/`;
  };

  useEffect(() => {
    const fetchPosts = async (accessToken) => {
      try {
        const queryString = toQueryString({
          typeSMId: smTypes.instagramPost, // Assuming `typeSMId` for Instagram posts is defined
        });

        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/dash/pro-sm/list?${queryString}`,
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );

        if (response.status === 200 && response.data && response.data.success) {
          setPosts(response.data.smLinks); // Update state with fetched posts
        } else {
          const errorMsg =
            response.data.message || "Failed to fetch the social media links.";
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
        }
      } catch (error) {
        console.error("Error while fetching all the Instagram posts", error);
      }
    };

    if (accessToken) {
      fetchPosts(accessToken); // Call the fetch function if accessToken is available
    }

    return () => {};
  }, [accessToken]);

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Manage Instagram Posts</h3>

      <div className={styles.instructions}>
        <h4>How to Add Your Instagram Post:</h4>
        <ul>
          <li>Login to your Instagram account.</li>
          <li>Upload a new post or reel, or select an existing one.</li>
          <li>Right-click on the post or reel and select "Copy Link."</li>
          <li>Paste the link in the field below and click "Add."</li>
        </ul>
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <div className={styles.form}>
        <input
          type="text"
          name="url"
          placeholder="Enter Instagram post URL"
          value={postData.url}
          onChange={handleInputChange}
          className={styles.input}
        />
        <button
          onClick={addPost}
          className={styles.button}
          disabled={isLoading}
        >
          {isLoading ? "Saving..." : editIndex !== null ? "Update" : "Add"}
        </button>
      </div>

      <div className={styles.postsGrid}>
        {posts.map((post, index) => (
          <div key={index} className={styles.postBox}>
            <iframe
              className={styles.embed}
              src={getEmbedUrl(post.url)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={`Instagram Post ${index}`}
            ></iframe>
            <div className={styles.postInfo}>
              <p>
                <a href={post.url} target="_blank" rel="noopener noreferrer">
                  {post.url}
                </a>
              </p>
              <div className={styles.actions}>
                {/* <button
                  onClick={() => editPost(index)}
                  className={styles.editBtn}
                >
                  Edit
                </button> */}
                <button
                  onClick={() => handleDeleteClick(post.id)}
                  className={styles.deleteBtn}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal for delete confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Instagram reel?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SocialPostManager;
